import { CSSProperties, memo, useMemo } from "react";
import { List } from "../list";
import { isEqual } from "libs/predicates";
import {
  DeleteDraftEntryAction,
  EntryActions,
  entryCSSClasses,
  EntryTimestamp,
  OtherCommandEntryAction,
  PrivateEntryIcon,
  Recipients,
  Summary,
  useShouldShowChannelLabels,
} from "./layout";
import { GroupLabels, LabelChip, LabelContainer } from "../LabelChip";
import { PointerWithRecord } from "libs/schema";
import { useDraft } from "~/hooks/useDraft";
import { useThread } from "~/hooks/useThread";
import { isModKeyActive } from "~/environment/command.service";
import { openComposeNewThreadDialog } from "~/page-dialogs/page-dialog-state";
import { htmlToText } from "libs/htmlToText";
import { useDraftRecipientNames } from "~/hooks/useDraftRecipientNames";
import { useAsPointerWithRecord } from "~/hooks/useAsPointerWithRecord";
import { cx } from "@emotion/css";
import { ParentComponent } from "~/utils/type-helpers";
import { ClientEnvironment } from "~/environment/ClientEnvironment";

/* -------------------------------------------------------------------------------------------------
 * onDraftSelect
 * -----------------------------------------------------------------------------------------------*/

export async function onDraftSelect(
  environment: Pick<ClientEnvironment, "router">,
  props: {
    event: React.MouseEvent | React.KeyboardEvent | MouseEvent | KeyboardEvent;
    draft: PointerWithRecord<"draft">;
    threadRoutePrefix?: string;
  },
) {
  let threadRoutePrefix = props.threadRoutePrefix || "";

  if (!threadRoutePrefix.endsWith("/")) {
    threadRoutePrefix += "/";
  }

  if (isModKeyActive(props.event)) {
    if (!props.draft.record.is_reply) {
      const url = new URL(location.href);
      url.searchParams.set("compose", props.draft.id);
      return environment.router.navigate(url, {
        openInNewTab: true,
      });
    }

    return environment.router.navigate(`${threadRoutePrefix}threads/${props.draft.record.thread_id}`, {
      openInNewTab: true,
    });
  }

  if (!props.draft.record.is_reply) {
    return openComposeNewThreadDialog(environment, props.draft.id);
  }

  return environment.router.navigate(`${threadRoutePrefix}threads/${props.draft.record.thread_id}`);
}

/* -------------------------------------------------------------------------------------------------
 * DraftEntry
 * -----------------------------------------------------------------------------------------------*/

export const DraftEntry: ParentComponent<{
  draftId: string;
  relativeOrder: number;
  style?: CSSProperties;
}> = memo(({ draftId, relativeOrder, style }) => {
  const shouldShowChannelLabels = useShouldShowChannelLabels();

  const [draft] = useDraft(draftId);
  const [thread] = useThread(draft?.thread_id);

  const text = useMemo(() => {
    if (!draft?.body_html) return "";
    return htmlToText(draft.body_html);
  }, [draft?.body_html]);

  const entryData = useAsPointerWithRecord("draft", draft);

  const recipientGroupIds = useMemo(() => {
    if (!draft) return [];

    return draft.to.filter((r) => r.type === "GROUP").map((r) => r.id);
  }, [draft?.to]);

  if (!draft || !entryData) return null;

  const isPrivateThread = draft.new_thread_visibility === "PRIVATE" || thread?.visibility === "PRIVATE";

  const subject = draft.new_thread_subject || thread?.subject || "";

  return (
    <List.Entry<PointerWithRecord<"draft">> id={draftId} data={entryData} relativeOrder={relativeOrder}>
      <div role="listitem" className={cx(entryCSSClasses, "DraftEntry")} style={style}>
        <Recipients className="w-[240px]" nonTruncatedSuffix={isPrivateThread && <PrivateEntryIcon />}>
          <div className="truncate">
            <span className="text-green-9 mr-3">Draft</span>
            <RecipientNames messageId={draftId} />
          </div>
        </Recipients>

        <Summary
          subject={subject}
          formatAsReply={draft.is_reply}
          details={isPrivateThread ? "private message" : text}
        />

        <LabelContainer>
          {shouldShowChannelLabels && <GroupLabels groupIds={recipientGroupIds} />}

          {draft.type === "EMAIL" && <LabelChip tooltip="This is an email thread">#Email</LabelChip>}
        </LabelContainer>

        <EntryActions defaultComponent={<EntryTimestamp datetime={draft.created_at} />}>
          <DeleteDraftEntryAction />
          <OtherCommandEntryAction />
        </EntryActions>
      </div>
    </List.Entry>
  );
}, isEqual);

/* -----------------------------------------------------------------------------------------------*/

const RecipientNames: ParentComponent<{ messageId: string }> = (props) => {
  const [names, { isLoading }] = useDraftRecipientNames(props.messageId);

  const recipientNames =
    names.length > 0 ? ` to ${names.join(", ")}`
    : isLoading ? " to loading..."
    : names.length === 0 ? ""
    : " to unknown";

  return <>{recipientNames}</>;
};

/* -----------------------------------------------------------------------------------------------*/
